import type { BasisPoints, Money, Qty } from '@pos/types';
import { QTY_SCALE } from '@pos/types';

/** Integer division rounded half away from zero (commercial rounding). */
export function divRound(numerator: number, denominator: number): number {
  if (denominator === 0) throw new RangeError('Division by zero');
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  return sign * Math.floor((2 * n + d) / (2 * d)) || 0;
}

function assertSafe(value: number, what: string): number {
  if (!Number.isSafeInteger(value)) throw new RangeError(`${what} is out of safe integer range`);
  return value;
}

/** Amount for `quantity` (milli-units) at `unitPrice` per whole unit. */
export function lineAmount(unitPrice: Money, quantity: Qty): Money {
  return divRound(assertSafe(unitPrice * quantity, 'Line amount'), QTY_SCALE);
}

/** `rate` basis points of `amount`, rounded. */
export function percentOf(amount: Money, rate: BasisPoints): Money {
  return divRound(assertSafe(amount * rate, 'Percent amount'), 10_000);
}

/** Tax contained in a tax-inclusive `gross` amount. */
export function inclusiveTax(gross: Money, rate: BasisPoints): Money {
  if (rate <= 0) return 0;
  return divRound(assertSafe(gross * rate, 'Tax amount'), 10_000 + rate);
}

/**
 * Split `amount` across `weights` so the parts are integers that sum exactly to
 * `amount` (largest-remainder method). Used to spread a cart discount over lines.
 */
export function allocate(amount: Money, weights: readonly number[]): Money[] {
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight <= 0 || amount === 0) return weights.map(() => 0);

  const exact = weights.map((w) => (amount * w) / totalWeight);
  const parts = exact.map((x) => Math.floor(x));
  const remainder = amount - parts.reduce((sum, p) => sum + p, 0);

  // The remainder is always smaller than the number of parts: hand out one
  // unit each to the parts with the largest fractional loss.
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order.slice(0, remainder)) parts[i] = (parts[i] ?? 0) + 1;
  return parts;
}

export interface CurrencyFormat {
  /** Number of minor-unit digits (UZS: 0, USD: 2). */
  scale: number;
  symbol: string;
  symbolPosition: 'before' | 'after';
}

export const CURRENCIES: Record<string, CurrencyFormat> = {
  UZS: { scale: 0, symbol: 'сум', symbolPosition: 'after' },
  USD: { scale: 2, symbol: '$', symbolPosition: 'before' },
  EUR: { scale: 2, symbol: '€', symbolPosition: 'after' },
  RUB: { scale: 2, symbol: '₽', symbolPosition: 'after' },
  KZT: { scale: 2, symbol: '₸', symbolPosition: 'after' },
};

/** Non-breaking space: keeps "27 900 сум" on one line. */
const NBSP = '\u00A0';

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Formats a number of minor units without currency symbol: 27900 → "27 900". */
export function formatAmount(amount: Money, currency = 'UZS'): string {
  const { scale } = CURRENCIES[currency] ?? { scale: 0 };
  const negative = amount < 0;
  const abs = Math.abs(amount);
  const factor = 10 ** scale;
  const whole = groupThousands(String(Math.floor(abs / factor)));
  const fraction = scale > 0 ? `.${String(abs % factor).padStart(scale, '0')}` : '';
  return `${negative ? '−' : ''}${whole}${fraction}`;
}

/** Formats money with the currency symbol: 27900 → "27 900 сум". */
export function formatMoney(amount: Money, currency = 'UZS'): string {
  const meta = CURRENCIES[currency];
  const value = formatAmount(amount, currency);
  if (!meta) return `${value}${NBSP}${currency}`;
  return meta.symbolPosition === 'before'
    ? `${meta.symbol}${value}`
    : `${value}${NBSP}${meta.symbol}`;
}

/**
 * Parses cashier input ("27 900", "12.50", "12,5") into minor units.
 * Returns null for anything that is not a valid non-negative amount.
 */
export function parseMoney(input: string, currency = 'UZS'): Money | null {
  const { scale } = CURRENCIES[currency] ?? { scale: 0 };
  const cleaned = input.replace(/[\s\u00A0]/g, '').replace(',', '.');
  const match = /^(\d+)(?:\.(\d*))?$/.exec(cleaned);
  if (!match) return null;
  const fraction = match[2] ?? '';
  if (fraction.length > scale) return null;
  const value = Number(match[1]) * 10 ** scale + Number(fraction.padEnd(scale, '0') || 0);
  return Number.isSafeInteger(value) ? value : null;
}
